import { CommonModule } from '@angular/common';
import { afterRenderEffect, ElementRef, viewChild, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { User } from 'src/app/models/user.model';
import { ContractorsService } from 'src/app/services/contractors.service';
import { ConversationMessagesService } from 'src/app/services/conversation-messages.service';
import { UsersService } from 'src/app/services/users.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { Roles } from 'src/app/utility/role';
import { convertTimestamp } from 'src/app/utility/time';
import {
  ComposeAudience,
  ConversationMessage,
  ConversationThread,
  ConversationType,
} from './messages-workspace.model';

import {isConversationUnread} from './conversation-read-state';

interface ConversationListThreadEntry {
  kind: 'thread';
  key: string;
  thread: ConversationThread;
  updatedAt: Date | null;
}

interface ConversationListGroupEntry {
  kind: 'group';
  key: string;
  groupId: string;
  label: string;
  threads: ConversationThread[];
  updatedAt: Date | null;
}

type ConversationListEntry = ConversationListThreadEntry | ConversationListGroupEntry;
type ConversationFolder = 'inbox' | 'sent';

const adminRoles = [Roles.admin, Roles.supervisor];
const excludedEmployeeRoles = [Roles.admin, Roles.superAdmin, Roles.manager, Roles.partner, Roles.premiumPartner, Roles.registered];

@Component({
  selector: 'app-message-workspace-dialog',
  host: {'[style.--app-modal-max-height]': 'mobileViewportHeight()'},

  templateUrl: './messages-workspace.dialog.html',
  styleUrls: ['./messages-workspace.dialog.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    ModalShellComponent,
    ReactiveFormsModule,
  ],
})
export class MessageWorkspaceDialogComponent {
  private contractorsService = inject(ContractorsService);
  private conversationMessagesService = inject(ConversationMessagesService);
  private formBuilder = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);

  readonly compose = input(false);
  readonly mobile = signal(false);
  readonly conversationOpen = signal(false);
  readonly mobileConversation = computed(() => this.mobile() && this.conversationOpen() && !this.composerOpen() && Boolean(this.selectedThread()));
  readonly history = viewChild<ElementRef<HTMLElement>>('messageHistory');
  private scrollToNewest = false;
  readonly mailbox = viewChild<ElementRef<HTMLElement>>('mailbox');
  private mailboxScrollTop = 0;
  private restoreMailboxScroll = false;
  readonly mobileMailbox = computed(() => this.showArchived() ? 'archive' : this.folder());
  readonly mobileViewportHeight = signal<string | null>(null);

  readonly folder = signal<ConversationFolder>('inbox');
  readonly searchTerm = signal('');
  readonly showArchived = signal(false);
  readonly selectedEntryKey = signal<string | null>(null);
  readonly selectedGroupedThreadId = signal<string | null>(null);
  readonly composerOpen = signal(false);
  readonly sending = signal(false);
  readonly recipientSearchTerm = signal('');

  readonly currentUser = computed(() => this.usersService.currentUserS());
  readonly isAdmin = computed(() => adminRoles.includes(this.usersService.role()));
  readonly activeContractor = computed(() => this.contractorsService.contractorS());

  readonly composeForm = this.formBuilder.group({
    title: [''],
    type: ['message' as ConversationType, Validators.required],
    audience: ['single' as ComposeAudience, Validators.required],
    recipientIds: [[] as string[]],
    message: ['', Validators.required],
  });

  readonly replyForm = this.formBuilder.group({
    message: ['', Validators.required],
  });

  readonly threads = toSignal(
    this.usersService.getCurrentUser().pipe(
      filter((user) => user != null),
      switchMap((user) => this.conversationMessagesService.getThreadsForUser(user.guid)),
    ),
    { initialValue: [] as ConversationThread[] },
  );

  readonly admins = toSignal(
    toObservable(computed(() => ({ user: this.currentUser(), contractor: this.activeContractor()?.guid }))).pipe(
      switchMap(({ user, contractor }) => {
        if (!user) {
return of([] as User[]);
}
        if (user.role !== Roles.worker) {
return this.usersService.getList(true, { roles: [Roles.admin, Roles.supervisor] });
}
        const organization = contractor ?? (user.contractors?.length === 1 ? user.contractors[0] : null);
        return organization && user.contractors?.includes(organization)
          ? this.usersService.getMyList(`admins?contractor=${encodeURIComponent(organization)}`)
          : of([] as User[]);
      }),
    ),
    { initialValue: [] as User[] },
  );

  readonly users = toSignal(this.usersService.getList(true), { initialValue: [] as User[] });
  readonly selectedRecipientIds = toSignal(
    this.composeForm.controls.recipientIds.valueChanges.pipe(
      startWith(this.composeForm.controls.recipientIds.value ?? []),
      map((value) => Array.isArray(value) ? value : [value].filter(Boolean)),
    ),
    { initialValue: [] as string[] },
  );

  readonly employeeRecipients = computed(() => {
    const currentUser = this.currentUser();
    const contractorGuid = this.activeContractor()?.guid ?? currentUser?.contractors?.[0] ?? null;
    return this.users().filter((user) =>
      user.guid !== currentUser?.guid
      && !user.inactive
      && !excludedEmployeeRoles.includes(user.role)
      && (contractorGuid ? user.contractors?.includes(contractorGuid) : true),
    );
  });

  readonly recipientOptions = computed(() => {
    if (this.isAdmin()) {
      return this.employeeRecipients();
    }

    const currentUser = this.currentUser();
    const contractor = this.activeContractor()?.guid ?? (currentUser?.contractors?.length === 1 ? currentUser.contractors[0] : null);
    return this.admins().filter((user) => user.guid !== currentUser?.guid
      && !user.inactive && !user.disabled
      && (currentUser?.role !== Roles.worker || Boolean(contractor && user.contractors?.includes(contractor) && adminRoles.includes(user.role))));
  });

  readonly listEntries = computed(() => {
    const currentUser = this.currentUser();
    const folder = this.folder();
    const mobileArchive = this.mobile() && this.showArchived();
    const showArchived = this.showArchived();
    const search = this.searchTerm().trim().toLowerCase();
    const visibleThreads = this.threads()
      .filter((thread) => showArchived ? this.isArchived(thread, currentUser?.guid) : !this.isArchived(thread, currentUser?.guid))
      .filter((thread) => mobileArchive ? true : folder === 'sent' ? thread.createdById === currentUser?.guid : thread.createdById !== currentUser?.guid)
      .filter((thread) => this.matchesSearch(thread, search, currentUser?.guid));

    const groupedEntries = new Map<string, ConversationThread[]>();
    const listEntries: ConversationListEntry[] = [];

    visibleThreads.forEach((thread) => {
      if (thread.groupId && thread.createdById === currentUser?.guid) {
        groupedEntries.set(thread.groupId, [...groupedEntries.get(thread.groupId) ?? [], thread]);
        return;
      }

      listEntries.push({
        kind: 'thread',
        key: `thread:${thread.guid}`,
        thread,
        updatedAt: this.asDate(thread.updatedAt ?? thread.lastMessage?.createdAt),
      });
    });

    groupedEntries.forEach((threads, groupId) => {
      if (threads.length <= 1) {
        const [thread] = threads;
        listEntries.push({
          kind: 'thread',
          key: `thread:${thread.guid}`,
          thread,
          updatedAt: this.asDate(thread.updatedAt ?? thread.lastMessage?.createdAt),
        });
        return;
      }

      const sortedThreads = [...threads].sort((a, b) => this.getMillis(b.updatedAt) - this.getMillis(a.updatedAt));
      listEntries.push({
        kind: 'group',
        key: `group:${groupId}`,
        groupId,
        label: sortedThreads[0].groupLabel || sortedThreads[0].title || this.translate.instant('messagesWorkspace.grouped.defaultTitle'),
        threads: sortedThreads,
        updatedAt: this.asDate(sortedThreads[0].updatedAt ?? sortedThreads[0].lastMessage?.createdAt),
      });
    });

    return listEntries.sort((a, b) => this.getMillis(b.updatedAt) - this.getMillis(a.updatedAt));
  });

  readonly selectedEntry = computed(() =>
    this.listEntries().find((entry) => entry.key === this.selectedEntryKey()) ?? null);

  readonly selectedThread = computed(() => {
    const entry = this.selectedEntry();
    if (!entry) {
      return null;
    }

    if (entry.kind === 'thread') {
      return entry.thread;
    }

    const preferredThreadId = this.selectedGroupedThreadId();
    return entry.threads.find((thread) => thread.guid === preferredThreadId) ?? entry.threads[0] ?? null;
  });

  readonly selectedThreadMessages = toSignal(
    toObservable(this.selectedThread).pipe(
      switchMap((thread) => thread?.guid ? this.conversationMessagesService.getMessages(thread.guid) : of([] as ConversationMessage[])),
    ),
    { initialValue: [] as ConversationMessage[] },
  );

  readonly groupedReadSummary = computed(() => {
    const entry = this.selectedEntry();
    if (entry?.kind !== 'group') {
      return null;
    }

    const total = entry.threads.length;
    const read = entry.threads.filter((thread) => this.hasRecipientRead(thread)).length;
    return { total, read };
  });

  readonly selectedComposeRecipients = computed(() => {
    const idSet = new Set(this.selectedRecipientIds());
    return this.recipientOptions().filter((user) => idSet.has(user.guid));
  });

  readonly filteredRecipientOptions = computed(() => {
    const search = this.recipientSearchTerm().trim().toLowerCase();
    const selectedIds = new Set(this.selectedComposeRecipients().map((user) => user.guid));

    return this.recipientOptions()
      .filter((user) => !selectedIds.has(user.guid))
      .filter((user) => search.length === 0 || user.displayName.toLowerCase().includes(search))
      .slice(0, 8);
  });

  private asDate(value?: Date | Timestamp | null) {
    return convertTimestamp(value) ?? null;
  }

  private getMillis(value?: Date | Timestamp | null) {
    return this.asDate(value)?.getTime() ?? 0;
  }

  constructor() {
    // The visual viewport shrinks for the mobile keyboard even when layout vh does not.
    const viewport = window.visualViewport;
    const fitViewport = () => {
      this.mobile.set(window.innerWidth <= 767);
      this.mobileViewportHeight.set(this.mobile() ? `${viewport?.height ?? window.innerHeight}px` : null);
    };
    fitViewport();
    window.addEventListener('resize', fitViewport);
    viewport?.addEventListener('resize', fitViewport);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('resize', fitViewport);
      viewport?.removeEventListener('resize', fitViewport);
    });

    afterRenderEffect(() => {
      this.mobileViewportHeight();
      const mailboxContainer = this.mailbox()?.nativeElement.closest('.ui-modal-shell__content');
      if (this.mobile() && !this.conversationOpen() && !this.composerOpen() && this.restoreMailboxScroll && mailboxContainer) {
        mailboxContainer.scrollTop = this.mailboxScrollTop;
        this.restoreMailboxScroll = false;
      }
      const messages = this.selectedThreadMessages();
      const history = this.history()?.nativeElement;
      if (!history || !this.mobileConversation()) {
return;
}
      if (messages.length && messages[0].threadId === this.selectedThread()?.guid && (this.scrollToNewest || history.scrollHeight - history.scrollTop - history.clientHeight < 120)) {
        history.scrollTop = history.scrollHeight;
        this.scrollToNewest = false;
      }
    });

    effect(() => {
      if (this.compose()) {
        this.composerOpen.set(true);
      }
    });

    effect(() => {
      const entries = this.listEntries();
      const selectedKey = this.selectedEntryKey();

      if (this.composerOpen()) {
        return;
      }

      if (entries.length === 0) {
        this.selectedEntryKey.set(null);
        return;
      }

      if (!selectedKey || !entries.some((entry) => entry.key === selectedKey)) {
        this.selectedEntryKey.set(entries[0].key);
      }
    });

    effect(() => {
      const thread = this.selectedThread();
      const currentUser = this.currentUser();

      if (!thread || !currentUser || this.composerOpen() || (this.mobile() && !this.conversationOpen())) {
        return;
      }

      if (this.isUnread(thread, currentUser.guid)) {
        void this.conversationMessagesService.markAsRead(thread, currentUser.guid);
      }
    });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  openCompose() {
    this.composerOpen.set(true);
    this.recipientSearchTerm.set('');
    this.replyForm.reset({ message: '' });
    this.composeForm.reset({
      title: '',
      type: 'message',
      audience: this.isAdmin() ? 'single' : 'single',
      recipientIds: [],
      message: '',
    });
  }

  closeCompose() {
    this.composerOpen.set(false);
    this.recipientSearchTerm.set('');
  }

  onFolderChange(folder: ConversationFolder) {
    this.folder.set(folder);
  }

  selectMobileMailbox(mailbox: ConversationFolder | 'archive') {
    this.showArchived.set(mailbox === 'archive');
    if (mailbox !== 'archive') {
this.onFolderChange(mailbox);
}
  }

  toggleArchived() {
    this.showArchived.update((value) => !value);
  }

  backToList() {
    this.restoreMailboxScroll = true;
    this.conversationOpen.set(false);
  }

  growReply(event: Event) {
    if (!this.mobile()) {
return;
}
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, window.innerHeight * 0.2)}px`;
  }

  selectEntry(entry: ConversationListEntry) {
    if (this.mobile() && !this.conversationOpen()) {
this.mailboxScrollTop = this.mailbox()?.nativeElement.closest('.ui-modal-shell__content')?.scrollTop ?? 0;
}
    this.conversationOpen.set(true);
    this.scrollToNewest = true;
    this.composerOpen.set(false);
    this.selectedEntryKey.set(entry.key);
    if (entry.kind === 'group') {
      this.selectedGroupedThreadId.set(entry.threads[0]?.guid ?? null);
    } else {
      this.selectedGroupedThreadId.set(null);
    }
  }

  selectGroupedThread(thread: ConversationThread) {
    this.scrollToNewest = true;
    this.selectedGroupedThreadId.set(thread.guid);
  }

  toggleRecipientSelection(recipientGuid: string, checked: boolean) {
    const currentValue = this.composeForm.get('recipientIds')?.value ?? [];
    this.composeForm.get('recipientIds')?.setValue(
      checked
        ? [...new Set([...currentValue, recipientGuid])]
        : currentValue.filter((guid: string) => guid !== recipientGuid),
    );
  }

  addRecipient(recipientGuid: string) {
    this.toggleRecipientSelection(recipientGuid, true);
    this.recipientSearchTerm.set('');
  }

  removeRecipient(recipientGuid: string) {
    this.toggleRecipientSelection(recipientGuid, false);
  }

  async sendReply() {
    const currentUser = this.currentUser();
    const thread = this.selectedThread();
    const message = this.replyForm.get('message')?.value?.trim();

    if (!currentUser || !thread || !message) {
      this.replyForm.markAllAsTouched();
      return;
    }

    this.sending.set(true);
    try {
      this.scrollToNewest = true;
      await this.conversationMessagesService.sendMessage(thread, currentUser, message);
      this.replyForm.reset({ message: '' });
    } finally {
      this.sending.set(false);
    }
  }

  async submitCompose() {
    const currentUser = this.currentUser();
    const contractorGuid = this.activeContractor()?.guid ?? currentUser?.contractors?.[0];

    this.composeForm.markAllAsTouched();
    if (!currentUser || !contractorGuid) {
      return;
    }

    const formValue = this.composeForm.getRawValue();
    const recipients = this.resolveComposeRecipients(formValue.audience as ComposeAudience, formValue.recipientIds);
    if (recipients.length === 0 || !formValue.message?.trim()) {
      return;
    }

    this.sending.set(true);
    try {
      const savedThreadIds = await this.conversationMessagesService.createConversation({
        sender: currentUser,
        recipients,
        contractor: contractorGuid,
        type: formValue.type as ConversationType,
        title: formValue.title?.trim() ?? '',
        content: formValue.message.trim(),
        audience: formValue.audience as ComposeAudience,
        audienceLabel: this.getAudienceLabel(formValue.audience as ComposeAudience, recipients),
      });

      this.composerOpen.set(false);
      if (savedThreadIds.length > 1) {
        const groupedEntry = this.listEntries().find((entry) =>
          entry.kind === 'group' && entry.threads.some((thread) => savedThreadIds.includes(thread.guid)));
        if (groupedEntry) {
          this.selectedEntryKey.set(groupedEntry.key);
          this.selectedGroupedThreadId.set(savedThreadIds[0]);
        }
      } else if (savedThreadIds[0]) {
        this.selectedEntryKey.set(`thread:${savedThreadIds[0]}`);
      }
    } finally {
      this.sending.set(false);
    }
  }

  async toggleArchiveSelected() {
    const currentUser = this.currentUser();
    const entry = this.selectedEntry();
    if (!currentUser || !entry) {
      return;
    }

    const threads = entry.kind === 'group' ? entry.threads : [entry.thread];
    const shouldArchive = threads.some((thread) => !this.isArchived(thread, currentUser.guid));

    await Promise.all(threads.map(async (thread) =>
      this.conversationMessagesService.setArchived(thread, currentUser.guid, shouldArchive)));

    this.conversationOpen.set(false);
    this.selectedEntryKey.set(null);
    this.selectedGroupedThreadId.set(null);
  }

  getEntryTitle(entry: ConversationListEntry): string {
    if (entry.kind === 'group') {
      return entry.label;
    }
    return this.getThreadTitle(entry.thread);
  }

  getEntrySubtitle(entry: ConversationListEntry): string {
    if (entry.kind === 'group') {
      return this.translate.instant('messagesWorkspace.grouped.subtitle', { count: entry.threads.length });
    }
    return this.getThreadParticipantsLabel(entry.thread);
  }

  getEntryPreview(entry: ConversationListEntry): string {
    if (entry.kind === 'group') {
      return entry.threads[0]?.lastMessage?.content ?? '';
    }
    return entry.thread.lastMessage?.content ?? '';
  }

  getEntryUnread(entry: ConversationListEntry): boolean {
    const currentUser = this.currentUser();
    if (!currentUser) {
      return false;
    }

    if (entry.kind === 'group') {
      return entry.threads.some((thread) => this.isUnread(thread, currentUser.guid));
    }
    return this.isUnread(entry.thread, currentUser.guid);
  }

  getThreadTitle(thread: ConversationThread): string {
    const trimmedTitle = thread.title?.trim();
    if (trimmedTitle) {
      return trimmedTitle;
    }
    return this.getThreadParticipantsLabel(thread);
  }

  getThreadParticipantsLabel(thread: ConversationThread): string {
    const currentUser = this.currentUser();
    const otherParticipants = thread.participants.filter((participant) => participant.guid !== currentUser?.guid);
    return otherParticipants.map((participant) => participant.name).join(', ') || thread.createdByName;
  }

  getThreadTypeKey(thread: ConversationThread): string {
    return `messagesWorkspace.types.${thread.type}`;
  }

  getInitials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
  }

  displayDate(value?: Date | Timestamp | null) {
    return this.asDate(value);
  }

  isMine(message: ConversationMessage): boolean {
    return message.senderId === this.currentUser()?.guid;
  }

  hasRecipientRead(thread: ConversationThread): boolean {
    if (!thread.recipientId) {
      return false;
    }
    return Boolean(thread.lastReadAtBy?.[thread.recipientId]);
  }

  getReadSummaryLabel(thread: ConversationThread): string {
    const currentUser = this.currentUser();
    if (!currentUser) {
      return '';
    }

    if (thread.createdById === currentUser.guid && thread.recipientId) {
      const lastReadAt = thread.lastReadAtBy?.[thread.recipientId];
      if (lastReadAt) {
        return this.translate.instant('messagesWorkspace.readStatus.readAt');
      }
      return this.translate.instant('messagesWorkspace.readStatus.unread');
    }

    const myReadAt = thread.lastReadAtBy?.[currentUser.guid];
    return myReadAt
      ? this.translate.instant('messagesWorkspace.readStatus.readAt')
      : this.translate.instant('messagesWorkspace.readStatus.unread');
  }

  private resolveComposeRecipients(audience: ComposeAudience, recipientIds: string[] | string | null): User[] {
    const options = this.recipientOptions();
    const normalizedIds = Array.isArray(recipientIds) ? recipientIds : [recipientIds].filter(Boolean);
    if (!this.isAdmin()) {
      return options.filter((user) => normalizedIds.includes(user.guid)).slice(0, 1);
    }

    if (audience === 'all') {
      return options;
    }
    if (audience === 'selected') {
      return options.filter((user) => normalizedIds.includes(user.guid));
    }
    return options.filter((user) => normalizedIds.includes(user.guid)).slice(0, 1);
  }

  private getAudienceLabel(audience: ComposeAudience, recipients: User[]): string {
    if (!this.isAdmin()) {
      return recipients[0]?.displayName ?? '';
    }

    if (audience === 'all') {
      return this.translate.instant('messagesWorkspace.audience.allEmployees');
    }
    if (audience === 'selected') {
      return this.translate.instant('messagesWorkspace.audience.selectedEmployees');
    }
    return recipients[0]?.displayName ?? '';
  }

  private isArchived(thread: ConversationThread, userGuid?: string | null): boolean {
    if (!userGuid) {
      return false;
    }
    return thread.archivedBy?.includes(userGuid) ?? false;
  }

  private isUnread(thread: ConversationThread, userGuid: string): boolean {
    return isConversationUnread(thread, userGuid);
  }

  private matchesSearch(thread: ConversationThread, search: string, userGuid?: string | null): boolean {
    if (!search) {
      return true;
    }

    const haystack = [
      thread.title,
      thread.groupLabel,
      thread.recipientName,
      thread.lastMessage?.content,
      thread.context?.title,
      thread.context?.subtitle,
      ...thread.participants
        .filter((participant) => participant.guid !== userGuid)
        .map((participant) => participant.name),
    ].filter(Boolean).join(' ').toLowerCase();

    return haystack.includes(search);
  }
}
